import { db } from "@/lib/firebase";
import { chatModerationService } from "./chat-moderation.service";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  addDoc,
  serverTimestamp,
  doc,
  setDoc,
  getDocs,
  getDoc,
  Timestamp,
  limitToLast,
  deleteDoc,
  updateDoc,
} from "firebase/firestore";

import {
  Conversation,
  Contact,
  Message,
  MessageAttachment,
} from "@/types/messaging";

// Messaging service class
export class MessagingService {
  private static instance: MessagingService;
  private messageListeners = new Map<string, () => void>();
  private typingTimeouts = new Map<string, NodeJS.Timeout>();

  static getInstance(): MessagingService {
    if (!MessagingService.instance) {
      MessagingService.instance = new MessagingService();
    }
    return MessagingService.instance;
  }

  // Send a direct message
  async sendDirectMessage(
    senderId: string,
    senderName: string,
    recipientId: string,
    recipientName: string,
    content: string,
    attachments?: MessageAttachment[]
  ): Promise<void> {
    try {
      const response = await fetch("/api/messages", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          content,
          recipientId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to send message");
      }
    } catch (error) {
      console.error("Error sending direct message:", error);
      throw error;
    }
  }

  // Send a league message
  async sendLeagueMessage(
    senderId: string,
    senderName: string,
    leagueId: string,
    content: string,
    attachments?: MessageAttachment[]
  ): Promise<void> {
    try {
      const response = await fetch("/api/messages", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          content,
          leagueId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to send message");
      }
    } catch (error) {
      console.error("Error sending league message:", error);
      throw error;
    }
  }

  // Send league announcement
  async sendLeagueAnnouncement(
    senderId: string,
    senderName: string,
    leagueId: string,
    announcement: {
      title: string;
      content: string;
      type: "general" | "event" | "achievement" | "urgent";
      priority: "low" | "normal" | "high";
    }
  ): Promise<void> {
    // Format announcement as a message for now, since API doesn't support structured announcements yet
    const formattedContent = `**${announcement.title.toUpperCase()}**\n\n${
      announcement.content
    }`;

    await this.sendLeagueMessage(
      senderId,
      senderName,
      leagueId,
      formattedContent
    );
  }

  // Mark conversation as read
  async markAsRead(userId: string, conversationId: string): Promise<void> {
    if (!db) return;

    try {
      const lastReadRef = doc(db, "users", userId, "lastRead", conversationId);
      await setDoc(
        lastReadRef,
        {
          timestamp: serverTimestamp(),
        },
        { merge: true }
      );
    } catch (error) {
      console.error("Error marking as read:", error);
    }
  }

  // Listen to messages for a conversation
  subscribeToMessages(
    userId: string,
    conversationId: string,
    conversationType: "direct" | "league",
    callback: (messages: Message[]) => void
  ): () => void {
    if (!db) throw new Error("Firestore not initialized");

    const messagesRef = collection(db, "messages");
    let messagesQuery;

    if (conversationType === "league") {
      messagesQuery = query(
        messagesRef,
        where("leagueId", "==", conversationId),
        orderBy("timestamp", "asc"),
        limitToLast(100)
      );
    } else {
      messagesQuery = query(
        messagesRef,
        where("isDirectMessage", "==", true),
        where("participants", "array-contains", userId),
        orderBy("timestamp", "asc"),
        limitToLast(200)
      );
    }

    const unsubscribe = onSnapshot(
      messagesQuery,
      (snapshot) => {
        const messages: Message[] = [];

        snapshot.forEach((doc) => {
          const data = doc.data();
          const timestamp =
            data.timestamp instanceof Timestamp
              ? data.timestamp.toMillis()
              : Date.now();

          const message: Message = {
            id: doc.id,
            sender: {
              id: data.senderId,
              name: data.senderName,
              profileImage: data.senderAvatar,
            },
            recipient: data.recipientId
              ? {
                  id: data.recipientId,
                  name: "Recipient",
                }
              : undefined,
            content: data.content,
            timestamp: timestamp,
            leagueId: data.leagueId,
            isDirectMessage: data.isDirectMessage,
            isAnnouncement: data.isAnnouncement,
          };

          if (conversationType === "league") {
            if (message.leagueId === conversationId) {
              messages.push(message);
            }
          } else {
            const otherUserId = conversationId;
            if (
              (message.sender.id === userId &&
                message.recipient?.id === otherUserId) ||
              (message.sender.id === otherUserId &&
                message.recipient?.id === userId) ||
              (data.participants &&
                data.participants.includes(userId) &&
                data.participants.includes(otherUserId))
            ) {
              messages.push(message);
            }
          }
        });

        messages.sort((a, b) => a.timestamp - b.timestamp);
        callback(messages);
      },
      (error) => {
        console.warn("[MessagingService] Messages listener error:", error.code);
        if (error.code === "permission-denied") {
          callback([]);
        }
      }
    );

    const listenerId = `${conversationType}-${conversationId}`;
    this.messageListeners.set(listenerId, unsubscribe);

    return () => {
      unsubscribe();
      this.messageListeners.delete(listenerId);
    };
  }

  // Subscribe to real-time conversation updates
  subscribeToConversations(
    userId: string,
    callback: (conversations: Conversation[]) => void
  ): () => void {
    if (!db) throw new Error("Firestore not initialized");

    // We'll use a combined approach: listen to messages and user's leagues
    const messagesRef = collection(db, "messages");

    // Listen to all messages where user is a participant (for DMs) or user's league messages
    const messagesQuery = query(
      messagesRef,
      where("participants", "array-contains", userId),
      orderBy("timestamp", "desc"),
      limit(100)
    );

    const unsubscribeMessages = onSnapshot(
      messagesQuery,
      async () => {
        // When messages change, re-fetch conversations
        const conversations = await this.getConversations(userId);
        callback(conversations);
      },
      (error) => {
        console.warn(
          "[MessagingService] Conversations messages listener error:",
          error.code
        );
        if (error.code === "permission-denied") {
          callback([]);
        }
      }
    );

    // Also listen to user's leagues changes
    const userRef = doc(db, "users", userId);
    const unsubscribeUser = onSnapshot(
      userRef,
      async () => {
        const conversations = await this.getConversations(userId);
        callback(conversations);
      },
      (error) => {
        console.warn("[MessagingService] User doc listener error:", error.code);
        if (error.code === "permission-denied") {
          callback([]);
        }
      }
    );

    // Return combined unsubscribe function
    return () => {
      unsubscribeMessages();
      unsubscribeUser();
    };
  }

  // Get user's conversations
  async getConversations(userId: string): Promise<Conversation[]> {
    if (!db) throw new Error("Firestore not initialized");

    const conversations: Conversation[] = [];

    try {
      // 1. Get User's Leagues
      const userDoc = await getDoc(doc(db, "users", userId));
      const userData = userDoc.data();
      const leagueIds = userData?.leagues || [];

      for (const leagueId of leagueIds) {
        const leagueDoc = await getDoc(doc(db, "leagues", leagueId));
        if (leagueDoc.exists()) {
          const leagueData = leagueDoc.data();

          // Find user's role
          let currentUserRole: "admin" | "member" | "captain" = "member";
          if (leagueData.members) {
            const member = leagueData.members.find(
              (m: any) => m.golferId === userId || m.id === userId
            );
            if (member && member.role) {
              currentUserRole = member.role;
            }
          }

          // Calculate unread count for this league conversation
          let unreadCount = 0;
          let lastMessage: Conversation["lastMessage"] = undefined;

          try {
            // Get user's last read timestamp for this league
            const lastReadDoc = await getDoc(
              doc(db, "users", userId, "lastRead", leagueId)
            );
            const lastReadTimestamp = lastReadDoc.exists()
              ? lastReadDoc.data().timestamp?.toMillis?.() || 0
              : 0;

            // Query messages after last read
            const messagesQuery = query(
              collection(db, "messages"),
              where("leagueId", "==", leagueId),
              orderBy("timestamp", "desc"),
              limit(50)
            );
            const messagesSnapshot = await getDocs(messagesQuery);

            // Count unread and get last message
            for (const msgDoc of messagesSnapshot.docs) {
              const msgData = msgDoc.data();
              const msgTimestamp =
                msgData.timestamp instanceof Timestamp
                  ? msgData.timestamp.toMillis()
                  : Date.now();

              // Set last message from first result
              if (!lastMessage) {
                lastMessage = {
                  content: msgData.content,
                  senderId: msgData.senderId,
                  senderName: msgData.senderName,
                  timestamp: msgTimestamp,
                };
              }

              // Count as unread if after lastRead and not from current user
              if (
                msgTimestamp > lastReadTimestamp &&
                msgData.senderId !== userId
              ) {
                unreadCount++;
              }
            }
          } catch (msgError) {
            console.warn(
              "Error calculating unread count for league:",
              leagueId,
              msgError
            );
          }

          conversations.push({
            id: leagueId,
            type: "league",
            league: {
              id: leagueId,
              name: leagueData.name,
              memberCount: leagueData.members?.length || 0,
              currentUserRole: currentUserRole,
            },
            unreadCount,
            lastMessage,
          });
        }
      }

      // 2. Get Direct Conversations
      const dmsQuery = query(
        collection(db, "messages"),
        where("isDirectMessage", "==", true),
        where("participants", "array-contains", userId),
        orderBy("timestamp", "desc"),
        limit(50)
      );

      const dmsSnapshot = await getDocs(dmsQuery);
      const dmMap = new Map<
        string,
        { lastMessage: any; otherUserId: string }
      >();

      // First pass: get unique conversations and their last messages
      for (const docSnapshot of dmsSnapshot.docs) {
        const data = docSnapshot.data();
        const otherUserId =
          data.senderId === userId ? data.recipientId : data.senderId;

        if (otherUserId && !dmMap.has(otherUserId)) {
          const timestamp =
            data.timestamp instanceof Timestamp
              ? data.timestamp.toMillis()
              : Date.now();
          dmMap.set(otherUserId, {
            lastMessage: {
              content: data.content,
              senderId: data.senderId,
              senderName: data.senderName,
              timestamp: timestamp,
            },
            otherUserId,
          });
        }
      }

      // Second pass: batch fetch user details for all other users
      const otherUserIds = Array.from(dmMap.keys());
      if (otherUserIds.length > 0) {
        if (!db) throw new Error("Firestore not initialized");
        const dbInstance = db;
        // Firestore 'in' query supports max 30 items, so chunk if needed
        const chunks = [];
        for (let i = 0; i < otherUserIds.length; i += 30) {
          chunks.push(otherUserIds.slice(i, i + 30));
        }

        for (const chunk of chunks) {
          // Pre-fetch lastRead timestamps for all users in the chunk
          const lastReadMap = new Map<string, number>();
          await Promise.all(
            chunk.map(async (otherId) => {
              try {
                const lrDoc = await getDoc(
                  doc(dbInstance, "users", userId, "lastRead", otherId)
                );
                if (lrDoc.exists()) {
                  lastReadMap.set(otherId, lrDoc.data().timestamp?.toMillis?.() || 0);
                } else {
                  lastReadMap.set(otherId, 0);
                }
              } catch (e) {
                lastReadMap.set(otherId, 0);
              }
            })
          );

          const usersQuery = query(
            collection(dbInstance, "users"),
            where("id", "in", chunk)
          );
          const usersSnapshot = await getDocs(usersQuery);

          usersSnapshot.forEach((userDocSnapshot) => {
            const userData = userDocSnapshot.data();
            const dmData = dmMap.get(userDocSnapshot.id);

            if (dmData) {
              // Calculate unread count from the loaded messages snapshot
              let unreadCount = 0;
              const lastReadTimestamp = lastReadMap.get(userDocSnapshot.id) || 0;
              
              for (const docSnapshot of dmsSnapshot.docs) {
                const data = docSnapshot.data();
                const otherId = data.senderId === userId ? data.recipientId : data.senderId;
                if (otherId === userDocSnapshot.id) {
                  const msgTimestamp = data.timestamp instanceof Timestamp ? data.timestamp.toMillis() : Date.now();
                  if (msgTimestamp > lastReadTimestamp && data.senderId !== userId) {
                    unreadCount++;
                  }
                }
              }

              conversations.push({
                id: userDocSnapshot.id,
                type: "direct",
                contact: {
                  id: userDocSnapshot.id,
                  name: userData.displayName || userData.name || "Unknown User",
                  profileImage: userData.photoURL || userData.profileImage,
                  isOnline: false,
                },
                lastMessage: dmData.lastMessage,
                unreadCount: unreadCount,
              });
            }
          });
        }
      }

      // Sort conversations by last message timestamp
      conversations.sort((a, b) => {
        const aTime = a.lastMessage?.timestamp || 0;
        const bTime = b.lastMessage?.timestamp || 0;
        return bTime - aTime;
      });

      return conversations;
    } catch (error) {
      console.error("Failed to get conversations:", error);
      return [];
    }
  }

  async getContacts(userId: string): Promise<Contact[]> {
    if (!db) throw new Error("Firestore not initialized");

    const contactIds = new Map<string, Omit<Contact, "id" | "name">>();

    try {
      // 1. Get contacts from leagues the user is a member of
      const leaguesQuery = query(
        collection(db, "leagues"),
        where("memberGolferIds", "array-contains", userId)
      );
      const leaguesSnapshot = await getDocs(leaguesQuery);

      for (const leagueDoc of leaguesSnapshot.docs) {
        const leagueData = leagueDoc.data();
        const memberIds = leagueData.memberGolferIds || [];
        for (const memberId of memberIds) {
          if (memberId !== userId && !contactIds.has(memberId)) {
            contactIds.set(memberId, {
              connectionType: "league_member",
              connectionDetails: leagueData.name,
              isOnline: false, // Placeholder
            });
          }
        }
      }

      // 2. Get contacts from past matches
      const matchesQuery = query(
        collection(db, "matches"),
        where("playerIds", "array-contains", userId)
      );
      const matchesSnapshot = await getDocs(matchesQuery);
      for (const matchDoc of matchesSnapshot.docs) {
        const matchData = matchDoc.data();
        const playerIds = matchData.playerIds || [];
        for (const playerId of playerIds) {
          if (playerId !== userId && !contactIds.has(playerId)) {
            contactIds.set(playerId, {
              connectionType: "previous_match",
              connectionDetails: `Match on ${
                matchData.date?.toDate?.().toLocaleDateString() || "a past date"
              }`,
              isOnline: false,
            });
          }
        }
      }

      // 3. Batch fetch user details for all collected IDs
      const allContactIds = Array.from(contactIds.keys());
      if (allContactIds.length === 0) return [];

      const contacts: Contact[] = [];
      const chunks = [];
      for (let i = 0; i < allContactIds.length; i += 30) {
        chunks.push(allContactIds.slice(i, i + 30));
      }

      for (const chunk of chunks) {
        const usersQuery = query(
          collection(db, "users"),
          where("id", "in", chunk)
        );
        const usersSnapshot = await getDocs(usersQuery);
        usersSnapshot.forEach((userDoc) => {
          const userData = userDoc.data();
          const contactDetails = contactIds.get(userDoc.id);
          if (contactDetails) {
            contacts.push({
              id: userDoc.id,
              name: userData.displayName || userData.name || "Unknown User",
              profileImage: userData.profileImage,
              ...contactDetails,
            });
          }
        });
      }

      return contacts;
    } catch (error) {
      console.error("Failed to get contacts:", error);
      return []; // Return empty on error
    }
  }

  // Set typing indicator
  async setTyping(
    userId: string,
    conversationId: string,
    isTyping: boolean
  ): Promise<void> {
    if (!db) return;

    try {
      // Use a subcollection for typing status
      const typingRef = doc(db, "typing", `${conversationId}_${userId}`);

      if (isTyping) {
        await setDoc(typingRef, {
          userId,
          conversationId,
          timestamp: serverTimestamp(),
        });

        // Clear after 10 seconds
        const timeoutKey = `${conversationId}-${userId}`;
        if (this.typingTimeouts.has(timeoutKey)) {
          clearTimeout(this.typingTimeouts.get(timeoutKey));
        }

        const timeout = setTimeout(() => {
          this.setTyping(userId, conversationId, false);
          this.typingTimeouts.delete(timeoutKey);
        }, 10000);

        this.typingTimeouts.set(timeoutKey, timeout);
      } else {
        await deleteDoc(typingRef);

        const timeoutKey = `${conversationId}-${userId}`;
        if (this.typingTimeouts.has(timeoutKey)) {
          clearTimeout(this.typingTimeouts.get(timeoutKey));
          this.typingTimeouts.delete(timeoutKey);
        }
      }
    } catch (error) {
      console.error("Error setting typing status:", error);
    }
  }

  // Subscribe to typing indicators
  subscribeToTyping(
    conversationId: string,
    callback: (typingUsers: string[]) => void
  ): () => void {
    if (!db) return () => {};

    const typingRef = collection(db, "typing");
    const q = query(typingRef, where("conversationId", "==", conversationId));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const typingUsers: string[] = [];
        const now = Date.now();

        snapshot.forEach((doc) => {
          const data = doc.data();
          const timestamp =
            data.timestamp instanceof Timestamp ? data.timestamp.toMillis() : 0;

          // Check if status is recent (within last 15 seconds)
          if (now - timestamp < 15000) {
            typingUsers.push(data.userId);
          }
        });

        callback(typingUsers);
      },
      (error) => {
        console.warn("[MessagingService] Typing listener error:", error.code);
        if (error.code === "permission-denied") {
          callback([]);
        }
      }
    );

    return unsubscribe;
  }

  // League moderation features (delegated to ChatModerationService)
  async moderateMember(
    leagueId: string,
    adminId: string,
    memberId: string,
    action: "mute" | "unmute" | "ban" | "unban" | "promote" | "demote"
  ): Promise<void> {
    return chatModerationService.moderateMember(leagueId, adminId, memberId, action);
  }

  async deleteMessage(messageId: string, adminId: string): Promise<void> {
    return chatModerationService.deleteMessage(messageId, adminId);
  }

  async pinMessage(messageId: string, adminId: string): Promise<void> {
    return chatModerationService.pinMessage(messageId, adminId);
  }

  async getLeagueMembers(leagueId: string): Promise<any[]> {
    return chatModerationService.getLeagueMembers(leagueId);
  }

  // Clean up all listeners
  cleanup(): void {
    this.messageListeners.forEach((unsubscribe) => {
      unsubscribe();
    });
    this.messageListeners.clear();

    this.typingTimeouts.forEach((timeout) => {
      clearTimeout(timeout);
    });
    this.typingTimeouts.clear();
  }
}

// Export singleton instance
export const messagingService = MessagingService.getInstance();
